import React, { FC, ChangeEventHandler, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useGetReservationFlowCourseSchemeCourses } from 'src/hooks/api/reservation-flow/useGetReservationFlowCourseSchemeCourses';
import { RSCheckBox } from 'src/reservation-page/RSCheckBox';
import { RSShowMoreText } from 'src/reservation-page/RSShowMoreText';
import { RSLinkButton } from 'src/reservation-page/RSLinkButton';

export interface OptionsSelectModalContentProps {
  merchantPublicId: string;
  resourcePublicId: string;
  courseCanonicalId: number;
  optionIds: number[];
  onOptionIdsChange: (optionIds: number[]) => void;
}

export const OptionsSelectModalContent: FC<OptionsSelectModalContentProps> = ({
  merchantPublicId,
  resourcePublicId,
  courseCanonicalId,
  optionIds,
  onOptionIdsChange,
}) => {
  const intl = useIntl();

  // FIXME: course をバケツリレーで受け取るようにすれば、ここでの取得＆エラー処理は不要になる
  const { data: courses } = useGetReservationFlowCourseSchemeCourses({
    parameter: {
      merchant_public_id: merchantPublicId,
      resource_public_id: resourcePublicId,
    },
  });

  const course = useMemo(() => {
    if (!courses) {
      return undefined;
    }

    return courses.courses.find((course) => {
      return course.canonical_id === courseCanonicalId;
    });
  }, [courseCanonicalId, courses]);

  const onOptionInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const optionId = parseInt(event.target.value, 10);
      if (isNaN(optionId)) return;

      if (event.target.checked) {
        onOptionIdsChange([...optionIds, optionId]);
      } else {
        onOptionIdsChange(optionIds.filter((id) => id !== optionId));
      }
    },
    [optionIds, onOptionIdsChange],
  );

  if (!course) {
    return null;
  }

  return (
    <div className="space-y-2.5">
      <div>
        <div className="flex items-center">
          {course.primary_image_url && (
            <div className="mr-1.5 flex-none">
              <div className="h-[80px] w-[80px] overflow-hidden rounded-[8px]">
                <img
                  className="block h-full w-full object-cover"
                  src={course.primary_image_url}
                  alt=""
                />
              </div>
            </div>
          )}
          <div className="flex-auto">
            <div className="space-y-0.5">
              <div>
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {course.name}
                </div>
              </div>

              <div>
                {(() => {
                  const durationString = intl.formatNumber(
                    course.duration / 60,
                    {
                      style: 'unit',
                      unit: 'minute',
                    },
                  );

                  const lowestPrice = (() => {
                    if (
                      course.onsite_price === null &&
                      course.prepayment_price === null
                    ) {
                      return null;
                    } else if (
                      course.onsite_price !== null &&
                      course.prepayment_price !== null
                    ) {
                      return course.onsite_price < course.prepayment_price
                        ? course.onsite_price
                        : course.prepayment_price;
                    } else {
                      return course.onsite_price !== null
                        ? course.onsite_price
                        : course.prepayment_price;
                    }
                  })();

                  const isPriceUnique = (() => {
                    if (
                      course.onsite_price === null ||
                      course.prepayment_price === null
                    ) {
                      return true;
                    }

                    return course.onsite_price === course.prepayment_price;
                  })();

                  if (lowestPrice === null) {
                    return (
                      <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                        {durationString}
                      </div>
                    );
                  }

                  let priceString;
                  if (isPriceUnique) {
                    priceString = intl.formatNumber(lowestPrice, {
                      style: 'currency',
                      currency: 'JPY',
                    });
                  } else {
                    priceString = intl.formatMessage(
                      {
                        defaultMessage: '{price}〜',
                        id: 'gXBqz/',
                        description:
                          'src/templates/merchants/resources/book/course_type/MainTemplate/components/CourseInput/components/CourseInputItem/index.tsx',
                      },
                      {
                        price: intl.formatNumber(lowestPrice, {
                          style: 'currency',
                          currency: 'JPY',
                        }),
                      },
                    );
                  }

                  return (
                    <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                      {`${durationString} / ${priceString}`}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-50" />

      <div>
        <div className="space-y-2.5">
          {course.options.map((option) => {
            return (
              <div key={option.id}>
                <div className="mb-1">
                  <div className="inline-block">
                    <label className="flex cursor-pointer items-center">
                      {/* インライン要素のline-heightをなくすためfont-sizeを0に設定 */}
                      <div className="flex-none text-[0px]">
                        <RSCheckBox
                          value={option.id.toString()}
                          checked={optionIds.includes(option.id)}
                          onChange={onOptionInputChange}
                        />
                      </div>
                      <div className="rs-font font-gray-1000 text-13 pc:text-12 ml-1 font-bold">
                        {option.name}
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  {(() => {
                    const durationString = intl.formatNumber(
                      option.duration / 60,
                      {
                        style: 'unit',
                        unit: 'minute',
                      },
                    );

                    if (option.price === null) {
                      return (
                        <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                          {durationString}
                        </div>
                      );
                    }

                    const priceString = intl.formatNumber(option.price, {
                      style: 'currency',
                      currency: 'JPY',
                    });

                    return (
                      <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">
                        {`${durationString} / ${priceString}`}
                      </div>
                    );
                  })()}
                </div>

                {option.description && (
                  <div className="rs-font text-13 text-gray-1000 pc:text-12 mt-0.5 whitespace-pre-wrap break-all font-normal">
                    <RSShowMoreText
                      text={option.description}
                      maxLine={2}
                      renderShowMoreButton={({ onClick }) => {
                        return (
                          <RSLinkButton onClick={onClick}>
                            {intl.formatMessage({
                              defaultMessage: 'さらに表示',
                              id: 'X90F3j',
                              description:
                                'src/templates/merchants/resources/book/course_type/MainTemplate/components/CourseInput/components/CourseInputItem/index.tsx',
                            })}
                          </RSLinkButton>
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
